import { App } from 'antd'
import axios, { AxiosError } from 'axios'
import { useEffect, useRef } from 'react'

/** One page mount can fire several denied lookups: toast once for the burst. */
const TOAST_COOLDOWN_MS = 3000

/**
 * A 403 tells us the admin lacks the permission that one endpoint requires. It
 * says nothing about the page they are on, and most 403s here come from
 * requests the admin never asked for — an async select mounting, a tab
 * prefetching, a hidden wizard step. Evicting the app to /403 on those threw
 * admins out of pages they are entitled to use.
 *
 * So a denied request stays local: the caller gets its rejection (an async
 * select renders empty, a list slice stops loading) and a single toast says
 * why. Page-level authorization stays where it belongs, in `<Page permissions>`
 * / `<HasAccess>`, checked against the admin's own permissions instead of
 * whichever background request happened to fail first.
 */
const ForbiddenRequestInterceptor = () => {
  const { message } = App.useApp()
  const lastToastAt = useRef<number>(0)

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Writes already surface their own failure (SBRM fireError, form
        // handlers), so only reads — which had no feedback but the redirect —
        // get the toast.
        const isRead = (error.config?.method ?? 'get').toLowerCase() === 'get'

        if (error.response?.status === 403 && isRead) {
          const now = Date.now()
          if (now - lastToastAt.current > TOAST_COOLDOWN_MS) {
            lastToastAt.current = now
            message.warning(
              'Some data could not be loaded: your account is missing the required permission.'
            )
          }
        }

        return Promise.reject(error)
      }
    )

    return () => axios.interceptors.response.eject(interceptorId)
  }, [message])

  return null
}

export default ForbiddenRequestInterceptor
